<script setup lang="tsx">
  import type {
    GetResponse,
    ResponseStatus,
    AdvancesDrawerType,
    AdvancePaymentResponse,
    OrgBankPaymentOption
  } from '~/types';
  import { PencilIcon } from '@heroicons/vue/solid';
  import { get, post } from '~/services/donationScreen';
  import InfoRow from '~/components/UI/Drawers/InfoRow.vue';
  import InfoSection from '~/components/UI/Drawers/InfoSection.vue';
  import DrawerLayout from '~/components/UI/Drawers/DrawerLayout.vue';
  import { checkValueAndLength } from '~/functions/stringUtilities';
  import RoleBasedLinkButton from '~/components/UI/Others/RoleBasedLinkButton.vue';
  import Attachments from '../../Others/Attachments.vue';
  import AuditLogs from '../RoutedDrawerComponents/AuditLogs.vue';
  import AttachmentUpdates from '../RoutedDrawerComponents/AttachmentUpdates.vue';
  import { formatToIndianCurrency } from '~/functions/currencyUtilities';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';
  import { formatDate, formatDateWithSlash } from '~/functions/dateTimeUtilities';
  import ApproveAdvanceModal from '../../Modals/ApproveAdvanceModal.vue';
  import DenyAdvanceModal from '../../Modals/DenyAdvanceModal.vue';
  import Approvals from '../RoutedDrawerComponents/Approvals.vue';
  import EditReasonModal from '../../Modals/EditReasonModal.vue';
  import type { CreateOrEditAdvanceResponse } from '~/types/advances/addNewAdvanceForm';
  import OverviewCard from '~/components/UI/Others/OverviewCard.vue';

  const { onClose, identifier } = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();

  const route = useRoute();
  const router = useRouter();
  const { role } = useAuth();
  const denyAdvanceModal = ref<boolean>(false);
  const approveAdvanceModal = ref<boolean>(false);
  const isModalVisible = ref<boolean>(false);
  const editUserStatus = ref<ResponseStatus>('idle');
  const advanceAgainstExpense = ref<boolean>(false);
  const alerts = ref<string>('');
  const submitForApprovalStatus = ref<string>('');
  const isFinalApprover = ref<boolean>(false);
  const isCurrentApprover = ref<boolean>(false);
  const isCancelled = ref<boolean>(false);

  const data = ref<AdvancesDrawerType>({} as AdvancesDrawerType);

  const responseState = ref<ResponseStatus>('idle');

  // Org banks bucketed by uid, so a payment's payment_bank_uid can be resolved to its
  // "{alias} - {account_no}" label for the Payments table's Bank column.
  const orgBanksList = ref<{ FCRA: OrgBankPaymentOption[]; 'Non-FCRA': OrgBankPaymentOption[] }>({
    FCRA: [],
    'Non-FCRA': []
  });
  const bankLabelByUid = computed(() => {
    const map = new Map<string, string>();
    [...orgBanksList.value.FCRA, ...orgBanksList.value['Non-FCRA']].forEach(bank => {
      map.set(bank.uid.toString(), bank.label);
    });
    return map;
  });
  const bankLabelForPayment = (payment: any) =>
    (payment?.payment_bank_uid && bankLabelByUid.value.get(payment.payment_bank_uid.toString())) || '-';

  const showDenyAdvanceModal = () => {
    denyAdvanceModal.value = true;
  };

  const showCancelAdvanceModal = () => {
    isModalVisible.value = true;
  };

  const hideDenyAdvanceModal = () => {
    denyAdvanceModal.value = false;
    fetchData(); // Refresh data after closing modal
  };

  const showApproveAdvanceModal = () => {
    approveAdvanceModal.value = true;
  };

  const hideApproveAdvanceModal = () => {
    approveAdvanceModal.value = false;
    fetchData(); // Refresh data after closing modal
  };

  const handleModalCancel = () => {
    isModalVisible.value = false;
  };

  const submitForApproval = async () => {
    const submitForApprovalResponse = await post<CreateOrEditAdvanceResponse>('/v1/submit_advance_for_approval', {
      data: { uid: identifier }
    });
    if (submitForApprovalResponse?.data?.status === 'success') {
      openSuccessfulCustomNotification(submitForApprovalResponse?.data?.message);
      fetchData();
      return router.push(`/advances?drawerFrom=allAdvances&identifier=${identifier}`);
    } else {
      openFailedCustomNotification(submitForApprovalResponse?.data?.message);
    }
  };

  const fetchData = async () => {
    responseState.value = 'pending';
    try {
      const response = await get<GetResponse<AdvancesDrawerType>>(`v1/get_advance/${identifier}`);
      if (response.status === 200) {
        data.value = response.data.data;
        advanceAgainstExpense.value = response?.data?.data?.selected_advance?.payment_stats?.advances_against_expense;
        submitForApprovalStatus.value = response?.data?.data?.selected_advance?.approval_status ?? '';
        alerts.value = response?.data?.data?.selected_advance?.alerts ?? '';
        isFinalApprover.value = response?.data?.data?.selected_advance?.is_final_approver;
        isCurrentApprover.value = response?.data?.data?.selected_advance?.is_current_approver;
        isCancelled.value = response?.data?.data?.selected_advance?.is_cancelled;
        responseState.value = 'success';
      } else {
        responseState.value = 'error';
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const submitHandler = async (data: any) => {
    const identifier = route.query.identifier ?? null;
    try {
      const modalData = {
        edit_reason: data.edit_reason,
        password: data.password,
        uid: identifier,
        tag_expense_against_advance: advanceAgainstExpense.value
      };
      editUserStatus.value = 'pending';
      const response = await post<any>(`/v1/cancel_advance`, {
        data: {
          ...modalData
        }
      });
      editUserStatus.value = 'success';
      if (response.data && response.data.status === 'success') {
        isModalVisible.value = false;
        openSuccessfulCustomNotification(response?.data?.message);
        fetchData();
        return router.push(`/advances?drawerFrom=allAdvances&identifier=${identifier}`);
      } else if (response?.data?.status === 'error') {
        openFailedCustomNotification(response?.data?.message);
      }
    } catch (error: any) {
      editUserStatus.value = 'error';
      console.error(error);
    }
  };

  const isEditDisabled = computed(() => {
    const rolesList = role.value || [];
    const isSubmitter = rolesList.includes('advance_submitter');
    const isNotSubmitted = submitForApprovalStatus.value === 'Not submitted for approval';
    const isApproverRole = rolesList.includes('advance_approver');
    const isAdmin = rolesList.includes('admin');
    const isAdvanceAdmin = rolesList.includes('advance_admin');
    const isAdvanceCurrentApprover = isCurrentApprover?.value;
    const isLastApprover = isFinalApprover?.value;
    const isApproved = submitForApprovalStatus.value === 'Approved';
    const isNoApprover = submitForApprovalStatus.value === '0 of 0';
    const isAdvanceCancelled = isCancelled?.value;
    const isAdvanceControlAdmin = rolesList.includes('advance_approval_control_admin');

    if (!isApproved && !isNotSubmitted && !isNoApprover && (isAdmin || isAdvanceAdmin) && isAdvanceControlAdmin) {
      return false;
    }

    if ((isSubmitter || isAdmin || isAdvanceAdmin) && isNoApprover) {
      return false;
    }

    if (isNotSubmitted && isSubmitter && isAdvanceCancelled) {
      return false;
    }

    if (isSubmitter && isNotSubmitted && !isAdvanceCancelled) {
      return false;
    }

    if (isAdvanceAdmin && isNotSubmitted) {
      return false;
    }

    if (isAdvanceAdmin && isApproved) {
      return false;
    }

    if (isAdmin && isNotSubmitted) {
      return false;
    }

    if (!isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return true;
    }

    if (isAdmin && isApproved) {
      return false;
    }

    if (isAdvanceAdmin && !isNotSubmitted && !isApproved) {
      return false;
    }

    if ((isApproverRole || isAdvanceAdmin || isAdmin) && isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isApproverRole && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isAdvanceCurrentApprover && isNotSubmitted && !isLastApprover) {
      return true;
    }

    if ((isNotSubmitted && isLastApprover) || isAdvanceCurrentApprover) {
      return true;
    }

    if (isApproved && isLastApprover) {
      return false;
    }

    return true;
  });

  const isSubmitDisabled = computed(() => {
    const rolesList = role.value || [];
    const isAdvanceCurrentApprover = isCurrentApprover?.value;
    const isSubmitter = rolesList.includes('advance_submitter');
    const isNotSubmitted = submitForApprovalStatus.value === 'Not submitted for approval';
    const isApproverRole = rolesList.includes('advance_approver');
    const isAdmin = rolesList.includes('admin');
    const isAdvanceAdmin = rolesList.includes('advance_admin');
    const isLastApprover = isFinalApprover?.value;
    const isApproved = submitForApprovalStatus.value === 'Approved' || submitForApprovalStatus.value === '0 of 0';
    const isAdvanceCancelled = isCancelled?.value;
    const isAdvanceControlAdmin = rolesList.includes('advance_approval_control_admin');

    if (isNotSubmitted && (isAdvanceAdmin || isAdmin) && isAdvanceControlAdmin) {
      return false;
    }

    if (!isNotSubmitted && !isAdvanceCancelled && !isApproved) {
      return true;
    }

    if (isNotSubmitted && isSubmitter) {
      return false;
    }

    if (isSubmitter && isNotSubmitted) {
      return false;
    }

    if (isAdvanceCurrentApprover && isNotSubmitted && !isLastApprover) {
      return false;
    }

    if (isSubmitter && isApproved) {
      return true;
    }

    if (isAdmin && isNotSubmitted) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover && isAdvanceCancelled) {
      return true;
    }

    if (isAdvanceAdmin && isNotSubmitted) {
      return false;
    }

    if ((isApproverRole || isAdvanceAdmin || isAdmin) && isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if ((isApproverRole || isAdvanceAdmin || isAdmin) && isApproved && isLastApprover) {
      return true;
    }

    if ((isNotSubmitted && isLastApprover) || isAdvanceCurrentApprover) {
      return true;
    }

    if (isNotSubmitted && !isAdvanceCancelled) {
      return true;
    }

    return true;
  });

  const isCancelDisabled = computed(() => {
    const rolesList = role.value || [];
    const isAdvanceCurrentApprover = isCurrentApprover?.value;
    const isSubmitter = rolesList.includes('advance_submitter');
    const isNotSubmitted = submitForApprovalStatus.value === 'Not submitted for approval';
    const isApproverRole = rolesList.includes('advance_approver');
    const isAdmin = rolesList.includes('admin');
    const isAdvanceAdmin = rolesList.includes('advance_admin');
    const isLastApprover = isFinalApprover?.value;
    const isApproved = submitForApprovalStatus.value === 'Approved' || submitForApprovalStatus.value === '0 of 0';

    if (isNotSubmitted && isSubmitter) {
      return false;
    }

    if (isSubmitter && isNotSubmitted) {
      return false;
    }

    if (isAdvanceCurrentApprover && isNotSubmitted && !isLastApprover) {
      return false;
    }

    if (isSubmitter && isApproved) {
      return true;
    }

    if (isAdmin && isNotSubmitted) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return true;
    }

    if (isAdvanceAdmin && isNotSubmitted) {
      return false;
    }

    if ((isApproverRole || isAdvanceAdmin || isAdmin) && isApproved && isLastApprover) {
      return false;
    }

    if ((isApproverRole || isAdvanceAdmin || isAdmin) && isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (!isNotSubmitted && !isApproved && isAdvanceCurrentApprover && isApproverRole) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted) {
      return false;
    }

    if ((isNotSubmitted && isLastApprover) || isAdvanceCurrentApprover) {
      return true;
    }

    if (isAdvanceAdmin && isApproved) {
      return false;
    }

    if (isAdmin && isApproved) {
      return false;
    }

    if (isAdvanceAdmin && !isNotSubmitted && !isApproved) {
      return false;
    }

    return true;
  });

  const getExpenseLength = () => {
    const length = data?.value?.selected_advance?.linked_expenses?.length ?? 0;
    return length > 0;
  };

  onMounted(fetchData);

  onMounted(async () => {
    try {
      const response = await get<AdvancePaymentResponse>('/v1/get_advance_payments_line_item');
      orgBanksList.value = response?.data?.org_banks_list || { FCRA: [], 'Non-FCRA': [] };
    } catch (error) {
      console.error('Error fetching org banks list:', error);
    }
  });
</script>

<template>
  <a-drawer
    v-if="identifier"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{
      padding: '12px',
      borderBottom: '1px solid #E4E4E7'
    }"
    title="Advance Information"
    :open="identifier !== null"
    width="1200"
    @close="onClose"
  >
    <template #extra>
      <div v-if="!data?.selected_advance?.is_cancelled" class="flex gap-2">
        <RoleBasedButton
          component-name="cancelActionButton"
          :disabled="isCancelDisabled"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-gray-500 border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="showCancelAdvanceModal"
        >
          Cancel Advance
        </RoleBasedButton>
        <RoleBasedLinkButton
          :to="{ path: '/advances/edit-advance', query: { identifier } }"
          component-name="editActionButton"
          :disabled="isEditDisabled"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-gray-500 border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
        >
          <PencilIcon class="h-4 w-4" />
          Edit Advance
        </RoleBasedLinkButton>
        <RoleBasedButton
          component-name="editActionButton"
          :disabled="isSubmitDisabled"
          active-class="sm-medium border-0 whitespace-nowrap font-sans text-sm rounded h-auto px-2 py-1.5 !leading-[21px] bg-blue-600 text-white !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] duration-200"
          disabled-class="sm-medium  whitespace-nowrap font-sans text-sm rounded h-auto px-3 py-2 !leading-[21px]   !border !border-gray-300 !bg-transparent text-gray-300 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] duration-200"
          @click="submitForApproval"
        >
          Submit for Approval
        </RoleBasedButton>
        <RoleBasedButton
          component-name="closeActionButton"
          :disabled="
            submitForApprovalStatus === 'Not submitted for approval' ||
            !data?.selected_advance?.is_current_approver ||
            (Boolean(alerts) && isFinalApprover)
          "
          active-class="!text-white hover:!text-white bg-red-700 hover:bg-red-600 disabled:!text-red-300 disabled:border-red-300 disabled:bg-red-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-red-700 hover:!border-red-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-red-300 gap-1 text-sm font-medium py-1.5 px-2 justify-center border !border-red-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="showDenyAdvanceModal"
        >
          Deny Advance
        </RoleBasedButton>
        <RoleBasedButton
          component-name="approveActionButton"
          :disabled="
            submitForApprovalStatus === 'Not submitted for approval' ||
            !data?.selected_advance?.is_current_approver ||
            (Boolean(alerts) && isFinalApprover)
          "
          active-class="!text-white hover:!text-white bg-green-700 hover:bg-green-600 disabled:!text-green-300 disabled:border-green-300 disabled:bg-green-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-green-700 hover:!border-green-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-green-500 gap-1 text-sm font-medium py-1.5 px-2 justify-center !bg-green-100 border !border-green-500 shadow-[0_1px_2px_0px_#0000000D]"
          @click="showApproveAdvanceModal"
        >
          Approve Advance
        </RoleBasedButton>
      </div>
      <p v-else class="text-base font-bold text-red-600">Advance is Cancelled</p>
    </template>
    <DrawerLayout
      :response-state="responseState"
      :main-display="`Advance to ${checkValueAndLength(data?.selected_advance?.payee_name)}` || '-'"
      :sub-display="checkValueAndLength(formatDate(data?.selected_advance?.created_date)) || '-'"
      :side-display="formatToIndianCurrency(+data?.selected_advance?.amount)"
      @close="onClose"
    >
      <template #column1>
        <div v-if="Boolean(alerts)" class="flex flex-col gap-3 p-3">
          <a-alert :message="alerts" type="warning" show-icon class="text-[14px]" />
        </div>
        <InfoSection>
          <InfoRow label="Payee Name" :value="checkValueAndLength(data?.selected_advance?.payee_name)" />
          <InfoRow label="Payee Bank" :value="checkValueAndLength(data?.selected_advance?.payee_bank)" />
          <InfoRow
            label="Request Date"
            :value="checkValueAndLength(formatDate(data?.selected_advance?.request_date))"
          />
          <InfoRow label="Description" :value="checkValueAndLength(data?.selected_advance?.description)" />
          <InfoRow label="Comments" :value="checkValueAndLength(data?.selected_advance?.internal_comments)" />
        </InfoSection>
        <div v-if="data?.selected_advance?.payments?.length > 0" class="p-3 border border-t border-b">
          <p class="text-sm leading-7 text-[#52525B] font-semibold mb-3">Payments</p>
          <div class="border border-zinc-300 rounded overflow-hidden">
            <table class="w-full table-fixed border-b border-collapse sm-medium rounded">
              <thead>
                <tr class="border-b bg-zinc-50">
                  <th class="text-left py-2 px-2 border-r font-medium w-[5%]">Sr</th>
                  <th class="text-left py-2 px-2 border-x font-medium w-[15%]">Date</th>
                  <th class="text-left py-2 px-2 border-x font-medium w-[14%]">Mode</th>
                  <th class="text-left py-2 px-2 border-l font-medium w-[14%]">Ref</th>
                  <th class="text-left py-2 px-2 border-l font-medium w-[14%]">Book of A/c</th>
                  <th class="text-left py-2 px-2 border-l font-medium w-[20%]">Bank</th>
                  <th class="text-right py-2 px-2 border-l font-medium w-[18%]">Amount Paid</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(paymentAdvances, index) in data?.selected_advance?.payments"
                  :key="paymentAdvances.payment_date"
                >
                  <td class="py-2 px-2 border-t border-r align-top break-words">{{ index + 1 }}</td>
                  <td class="py-2 px-2 border-t border-r align-top break-words">
                    {{ formatDate(paymentAdvances.payment_date, 'DD/MM/YYYY') }}
                  </td>
                  <td class="py-2 px-2 border-x border-t align-top break-words">{{ paymentAdvances.payment_mode }}</td>
                  <td class="py-2 px-2 border-t border-l align-top break-words">{{ paymentAdvances.payment_ref }}</td>
                  <td class="py-2 px-2 border-t border-l align-top break-words">
                    {{ paymentAdvances.payment_fcra_status }}
                  </td>
                  <td class="py-2 px-2 border-t border-l align-top break-words">
                    {{ bankLabelForPayment(paymentAdvances) }}
                  </td>
                  <td class="py-2 px-2 border-t border-l align-top break-words text-right">
                    {{ formatToIndianCurrency(paymentAdvances.payment_amount) }}
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="flex justify-end p-3">
              <div class="grid grid-cols-[auto_auto] gap-x-4">
                <!-- Total Amount -->
                <p class="text-right text-[#52525B] text-sm font-medium leading-7">Total Amount</p>
                <p class="text-right text-black font-medium text-base leading-7">
                  {{ formatToIndianCurrency(data?.selected_advance?.payment_stats?.amount_paid) }}
                </p>

                <!-- Balance Due -->
                <p class="text-right text-[#52525B] text-sm font-medium leading-7">Balance Payment Due (net of TDS)</p>
                <p class="text-right text-red-600 font-medium text-base leading-7">
                  {{
                    formatToIndianCurrency(
                      data?.selected_advance?.amount -
                        data?.selected_advance?.payment_stats?.tds -
                        data?.selected_advance?.payment_stats?.amount_paid
                    )
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div v-if="data?.selected_advance?.linked_expenses?.length > 0" class="p-3 border border-t border-b">
          <p class="text-sm leading-7 text-[#52525B] font-semibold mb-3">Applied to Expense</p>
          <div class="border border-zinc-300 rounded overflow-hidden">
            <table class="w-full border-b border-collapse sm-medium rounded">
              <thead>
                <tr class="border-b bg-zinc-50">
                  <th class="text-left py-2 px-3 border-r font-medium">Sr</th>
                  <th class="text-left py-2 px-3 border-x font-medium">Expense</th>
                  <th class="text-left py-2 px-3 border-x font-medium">Expense Amount</th>
                  <th class="text-left py-2 px-3 border-l font-medium">Amount Applied</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(expense, index) in data?.selected_advance?.linked_expenses" :key="index">
                  <td class="py-2 px-3 border-t border-r">{{ index + 1 }}</td>
                  <td class="py-2 px-3 border-t border-r">
                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-2">
                        <span class="text-[#3F3F46] text-sm font-medium leading-7 bg-[#F4F4F5] px-2 py-0.5 rounded">
                          {{ expense?.name }}
                        </span>
                        <a-tooltip placement="right" overlay-class-name="custom-tooltip">
                          <template #title>
                            <div class="flex flex-col w-full bg-white p-1">
                              <!-- <span
                                class="text-[#52525B] font-medium text-sm whitespace-nowrap max-w-[500px] text-clip overflow-hidden"
                              >
                                Expense for {{ expense.description }}
                              </span> -->
                              <span class="text-[#71717A] font-normal text-sm whitespace-nowrap">
                                Created on {{ formatDateWithSlash(expense.created_date) }} by {{ expense.created_user }}
                              </span>
                            </div>
                          </template>
                          <NuxtLink
                            :to="{
                              path: '/expenses',
                              query: { uid: expense?.expense_uid }
                            }"
                            class="text-blue-600 text-sm border-none !bg-none !box-shadow-none !font-semibold p-0 underline underline-offset-[4px]"
                          >
                            {{ expense?.expense_uid }}
                          </NuxtLink>
                        </a-tooltip>
                      </div>
                    </div>
                  </td>
                  <td class="py-2 px-3 border-x border-t">{{ formatToIndianCurrency(expense?.expense_amount) }}</td>
                  <td class="py-2 px-3 border-t border-l">
                    {{ formatToIndianCurrency(expense?.payment_amount_for_expense) }}
                  </td>
                </tr>
                <tr class="bg-white border-t font-bold">
                  <td colspan="3" class="text-right py-2 px-3 text-black">Total Applied</td>
                  <td class="py-2 px-3 border-l">
                    {{ formatToIndianCurrency(data?.selected_advance?.payment_stats?.total_payment_against_advance) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
      <template #column2>
        <div
          class="flex flex-col rounded bg-white border-zinc-200 justify-between items-center shadow-[0px_1px_3px_0px_#0000001A,0px_1px_3px_0px_#0000000F] border"
        >
          <div class="flex justify-between w-full p-3">
            <p class="text-sm font-medium text-[#52525B]">Total Advance Amount</p>
            <p class="text-sm font-medium text-black">
              {{ formatToIndianCurrency(data?.selected_advance?.amount) }}
            </p>
          </div>
          <div class="flex justify-between w-full py-1.5 px-3">
            <p class="text-sm font-medium text-[#52525B]">TDS</p>
            <p class="text-sm font-medium text-black">
              {{ formatToIndianCurrency(data?.selected_advance?.tds_to_deduct) }}
            </p>
          </div>
          <div class="flex justify-between w-full border-t border-r-0 border-l-0 border-b-0 py-3 px-3">
            <p class="text-sm font-medium text-[#52525B]">Payable</p>
            <p class="text-sm font-medium text-black">
              {{ formatToIndianCurrency(data?.selected_advance?.payment_stats?.payable) }}
            </p>
          </div>
          <div class="flex justify-between border border-t border-r-0 border-l-0 w-full p-3">
            <p class="text-sm font-medium text-[#52525B]">Total Paid amount</p>
            <p class="text-sm font-medium text-black">
              {{ formatToIndianCurrency(data?.selected_advance?.payment_stats?.amount_paid) }}
            </p>
          </div>
          <div class="flex justify-between w-full p-3">
            <p class="text-sm font-medium text-[#52525B]">Total applied to exp</p>
            <p class="text-sm font-medium text-black">
              {{ formatToIndianCurrency(data?.selected_advance?.payment_stats?.total_payment_against_advance) }}
            </p>
          </div>
          <div class="flex justify-between w-full p-3 border border-t border-b border-r-0 border-l-0">
            <p class="text-sm font-medium text-[#52525B]">Advance Pending Settlement</p>
            <p class="text-sm font-medium text-[#B91C1C]">
              {{
                formatToIndianCurrency(
                  data?.selected_advance?.payment_stats?.amount_paid -
                    data?.selected_advance?.payment_stats?.total_payment_against_advance
                )
              }}
            </p>
          </div>
        </div>
        <OverviewCard title="Advance Quick Links">
          <div class="flex flex-col gap-4">
            <div class="flex justify-between items-center">
              <div class="flex gap-2 items-center">
                <p
                  class="flex h-5 w-5 shrink-0 grow-0 items-center justify-center rounded-full bg-zinc-200 text-zinc-700 xs-medium"
                >
                  {{ data?.selected_advance?.linked_expenses?.map(e => e.expense_uid).length }}
                </p>
                <p class="sm-medium text-zinc-500">Expenses</p>
              </div>
              <div class="flex gap-3 items-center">
                <NuxtLink
                  v-if="getExpenseLength()"
                  class="p-0 border-none shadow-none !bg-transparent text-blue-700"
                  target="_blank"
                  :to="{
                    path: '/expenses',
                    query: { expenseUid: data?.selected_advance?.linked_expenses?.map(e => e.expense_uid).join(',') }
                  }"
                >
                  View
                </NuxtLink>
              </div>
            </div>
          </div>
        </OverviewCard>
        <Attachments :items="data?.selected_advance?.attachments" :table="data?.selected_advance?.table" />
        <AuditLogs :items="data?.audit_logs" title="Advance" />
        <AttachmentUpdates :items="data?.attachment_logs" />
        <Approvals
          :items="data?.selected_advance?.approvers_list"
          :creator="data?.selected_advance?.creator.email"
          :draft-status="data?.selected_advance?.draft_status"
          status-of="advance"
        />
      </template>
    </DrawerLayout>
  </a-drawer>
  <DenyAdvanceModal :visible="denyAdvanceModal" :close="hideDenyAdvanceModal" :advance-id="identifier" />
  <ApproveAdvanceModal
    :visible="approveAdvanceModal"
    :close="hideApproveAdvanceModal"
    :advance-id="identifier"
    redirect-to="/advances"
  />
  <EditReasonModal
    :visible="isModalVisible"
    :loading="editUserStatus === 'pending'"
    modal-title="Cancel Advance"
    :is-advance="true"
    :linked-expenses="
      data?.selected_advance?.linked_expenses?.length > 0
        ? data?.selected_advance?.linked_expenses?.map(e => e.expense_uid)
        : []
    "
    @update:cancel="handleModalCancel"
    @update:update="submitHandler"
  />
</template>

<style>
  @import '~/assets/css/donationDrawer.css';
  .custom-tooltip {
    max-width: 500px !important;
    width: 100% !important;
  }

  .custom-tooltip .ant-tooltip-inner {
    background-color: #ffffff !important;
    font-weight: 500;
    font-size: 13px;
    padding: 8px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    width: max-content !important;
  }

  .custom-tooltip .ant-tooltip-inner span {
    display: block;
    word-break: break-word;
    overflow-wrap: break-word;
    white-space: normal;
    width: 100%;
  }

  .custom-tooltip .ant-tooltip-content {
    width: max-content !important;
  }

  .custom-tooltip .ant-tooltip-arrow::before {
    background-color: #ffffff !important;
  }
</style>
